import './css/app.css';
import './css/global-reset.css';
import { ApolloProvider } from '@apollo/client/react';
import { apolloClient } from './helpers/apollo';
import { SocketProvider } from './context/socket-context';
import { GlobalProvider } from './context/global-context';
import { AuthProvider, useAuth } from './context/auth-context';
import { TwitchOverlay } from './twitch-overlay';
import { envVariables } from './helpers/env-variables';
import { InternalizationProvider } from './context/cms-context';
import { OverlayErrorBoundary } from './components/overlay-error-boundary/overlay-error-boundary';
import { useOverlayFailsafes } from './helpers/use-overlay-failsafes';
import { setFaroUser } from './helpers/faro';
import { getOverlaySettings, isPreviewMode } from './helpers/overlay-settings';
import { getUserIdFromUrl, isObsBrowserSource } from './helpers/user-id';
import { UrlBuilder } from './components/url-builder/url-builder';
import { logger, LogContext, trackEvent } from './helpers/logger';
import { useEffect } from 'react';

const UnAuthedView = () => {
  useEffect(() => {
    // the auth provider fills userId in an effect, give it a moment before calling the url broken
    const timeout = setTimeout(() => logger.warn(LogContext.OVERLAY, 'Overlay opened without a userId'), 1000)
    return () => clearTimeout(timeout)
  }, [])

  return (
    <div>
      <p>No User ID</p>
    </div>
  )
}

const AuthedApp = () => {
  const { userId } = useAuth()

  useEffect(() => {
    setFaroUser(userId)
    // the auth provider reads the url after mount, so skip the initial null render
    if (userId) {
      // layout and theme choices show which setups streamers actually use
      trackEvent('overlay_started', {
        referrer: document.referrer || undefined,
        theme: getOverlaySettings().theme,
        badgePosition: getOverlaySettings().positions.liveBadge,
        betsPosition: getOverlaySettings().positions.betList,
        qrPosition: getOverlaySettings().positions.referralQr,
        winAnimation: getOverlaySettings().winAnimation,
        winSound: getOverlaySettings().winSound,
        // the url builder's live preview, not a real stream
        preview: isPreviewMode(),
      })
    }
  }, [userId])

  if (!userId) return <UnAuthedView />

  return (
    <ApolloProvider client={apolloClient}>
      <SocketProvider wsUrl={envVariables.WS_URL} shouldConnect>
        <InternalizationProvider>
          <GlobalProvider>
            <TwitchOverlay />
          </GlobalProvider>
        </InternalizationProvider>
      </SocketProvider>
    </ApolloProvider>
  )
}

// opened in a normal browser without a userId, someone is setting up their overlay rather than streaming it.
// an obs source with a missing userId still gets the bare overlay so a setup page never ends up on stream
const isUrlBuilder = !getUserIdFromUrl() && !isObsBrowserSource()

export const App = () => {
  useOverlayFailsafes({ scheduledRefresh: !isUrlBuilder })

  return (
    <div className="app-wrapper">
      <OverlayErrorBoundary>
        {isUrlBuilder ? (
          <UrlBuilder />
        ) : (
          <AuthProvider>
            <AuthedApp />
          </AuthProvider>
        )}
      </OverlayErrorBoundary>
    </div>
  )
}
